import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import { FormattedMessage } from 'react-intl';
import Panel from 'components/Panel/Panel';
import MatchGroupColumns from './components/MatchGroupColumns';
import ImportResult from './components/ImportResult';
import InputFile from 'components/InputFile';
import Preloader from 'components/Preloader';
import ImportLayout from './components/ImportLayout';
// requests
import { importAdminPigGroups } from 'endpoints/admin/pigGroups';
// utils
import isEmpty from 'lodash.isempty';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import cn from 'classnames';
import download from 'downloadjs';
import Papa from 'papaparse';
// styles
import './GroupsImport.scss';
// assets
import downloadFile from './EveryPig_Group_Upload_Template.csv';

const matchColumns = ['name', 'farm_name', 'start_date'];

const requiredColumns = ['farm_name'];

const mappingColumns = {
  'group id': 'name',
  'pig group': 'name',
  'farm name': 'farm_name',
  'creation date': 'start_date',
  'start date': 'start_date',
};

const options = [
  { value: 'name', label: <FormattedMessage id="component.dataTable.headers.groupId" /> },
  { value: 'farm_name', label: <FormattedMessage id="component.dataTable.headers.farmName" /> },
  { value: 'start_date', label: <FormattedMessage id="component.groupCreate.creationDate" /> },
];

class GroupsImport extends Component {

  state = {
    selectedFile: null,
    step: 1,
    disabledNextButton: true,
    matched: {},
    isLoading: false,
    groupImportId: null,
    result: {},
    parsed: false,
    parsedData: [],
    errors: [],
  };

  downloadCSV = () => {
    return download(downloadFile, 'EveryPig_Group_Upload_Template.csv', 'text/csv');
  };

  fileSelectedHandler = (e) => {
    if (e.target.files[0]) {
      const selectedFile = e.target.files[0];
      this.setState({ selectedFile });
      this.fileParseHandler(selectedFile);
    }
  };

  fileParseHandler = (selectedFile) => {
    const self = this;
    Papa.parse(selectedFile, {
      header: true,
      preview: 5,
      trimHeaders: true,
      skipEmptyLines: true,
      complete({ data, errors }) {
        if (errors.length) {
          self.setState({ errors });
        } else {
          self.setState({ parsed: true, parsedData: data, disabledNextButton: false, errors: [] });
        }
      },
    });
  };

  handleBackButton = (e) => {
    const { step } = this.state;
    const { router } = this.context;
    e.preventDefault();
    if (step === 1) {
      router.push('/groups');
    } else {
      this.setState((prevState) => ({
        step: prevState.step - 1,
        selectedFile: null,
        disabledNextButton: true,
        result: {},
        matched: {},
        parsed: false,
        parsedData: [],
        isLoading: false,
        groupImportId: null,
        errors: [],
      }));
    }
  };

  handleNextButton = () => {
    const { selectedFile, matched, step } = this.state;
    const { router } = this.context;

    if (step === 1) {
      this.setState((prevState) => ({ step: prevState.step + 1, disabledNextButton: true }));
    }
    if (step === 2) {
      this.setState({ isLoading: true });
      const fd = new FormData();
      Object.keys(matched).forEach((item) => fd.append(`resource[csv_matchers][${item}]`, matched[item]));
      fd.append('resource[csv_file]', selectedFile);
      fd.append('resource[action]', 'creating');
      importAdminPigGroups(fd)
        .then(({ result, id }) => {
          this.setState((prevState) => ({
            step: prevState.step + 1,
            disabledNextButton: false,
            result,
            isLoading: false,
            groupImportId: id,
          }));
        })
        .catch((resp) => {
          this.setState({ isLoading: false });
          return toastResponseErrors(resp);
        });
    }
    if (step === 3) {
      router.push('/groups');
    }
  };

  setMatchedData = (matched) => {
    this.setState({ matched, disabledNextButton: isEmpty(matched) });
  };

  closeButtonHeader = () => this.context.router.push('/groups');

  renderResultMessage = () => {
    const { duplicates_count = 0, invalid_rows } = this.state.result;
    const invalidRowsCount = (invalid_rows || []).length;

    if ((invalidRowsCount > 0) && !duplicates_count) return (
      <FormattedMessage
        tagName="div"
        id="component.groupsImport.errors"
        values={{ count: invalidRowsCount, b: (msg) => <b>{msg}</b> }}
      />
    );

    if ((duplicates_count > 0) && !invalidRowsCount) return (
      <FormattedMessage
        tagName="div"
        id="component.groupsImport.duplicates"
        values={{ count: duplicates_count, b: (msg) => <b>{msg}</b> }}
      />
    );

    if ((duplicates_count > 0) && (invalidRowsCount > 0)) return (
      <FormattedMessage
        tagName="div"
        id="component.groupsImport.errorsAndDuplicates"
        values={{ errorsCount: invalidRowsCount, duplicatesCount: duplicates_count, b: (msg) => <b>{msg}</b> }}
      />
    );

    return null;
  };

  render() {
    const { user } = this.props;
    const { step, isLoading, parsed, groupImportId, disabledNextButton, parsedData, matched, result,
      errors } = this.state;
    return (
      <ImportLayout
        title={<FormattedMessage tagName="b" id="general.groupImport" />}
        user={user}
        closeButtonHeader={this.closeButtonHeader}
        handleNextButton={this.handleNextButton}
        handleBackButton={this.handleBackButton}
        disabledNextButton={disabledNextButton}
        step={step}
      >
        {step === 3 && (
          <div className="import-result-summary">
            <i className="check-icon fa fa-check-circle" />
            <FormattedMessage
              tagName="div"
              id="component.groupsImport.complete"
              values={{ count: result.created_count, b: (msg) => <b>{msg}</b> }}
            />
            {this.renderResultMessage()}
          </div>
        )}
        <Panel className="groupImportPanel">
          <Panel.Heading title={<FormattedMessage id="general.importFromCsvFile" />} />
          <Panel.Body noPadding className={cn('panelBody', { 'final-step': step === 3 })}>
            <div className="group-import-block">
              <Preloader isActive={isLoading} />
              {step === 1 && (
                <div className="upload-input-block">
                  <InputFile
                    label={<FormattedMessage id="general.uploadFile" />}
                    onChange={this.fileSelectedHandler}
                    accept=".csv"
                    explanatoryText={(
                      <FormattedMessage
                        id="general.uploadFile.hint"
                        values={{ link: (
                          <span className="download-link" onClick={this.downloadCSV}>
                            <FormattedMessage id="general.here" />
                          </span>
                        ),
                        }}
                      />
                    )}
                    explanatorySubText={<FormattedMessage tagName="i" id="general.removeDuplicates" />}
                  />
                  {parsed && (
                    <div className="success-message-block">
                      <span className="headline">
                        <i className="fa fa-check" /> <FormattedMessage id="general.success" />!&nbsp;
                      </span>
                      <FormattedMessage id="general.uploadSuccessMessage" />
                    </div>
                  )}
                  {errors.length > 0 && (
                    <div className="error-message">
                      <FormattedMessage id="general.invalidFileMessage" />
                    </div>
                  )}
                </div>
              )}
              {step === 2 && (
                <div>
                  <MatchGroupColumns
                    matched={matched}
                    setMatchData={this.setMatchedData}
                    parsedData={parsedData}
                    matchColumns={matchColumns}
                    requiredColumns={requiredColumns}
                    mappingColumns={mappingColumns}
                    options={options}
                  />
                  {!isEmpty(matched) && (
                    <div className="success-message-block ml-20">
                      <span className="headline">
                        <i className="fa fa-check" /> <FormattedMessage id="general.success" />!&nbsp;
                      </span>
                      <FormattedMessage id="general.matchingDone" />
                    </div>
                  )}
                </div>
              )}
              {step === 3 && groupImportId &&
                <ImportResult groupImportId={groupImportId} />}
            </div>
          </Panel.Body>
        </Panel>
      </ImportLayout>
    );
  }
}

GroupsImport.propTypes = {
  user: T.object,
};

GroupsImport.contextTypes = {
  router: T.object.isRequired,
};

export default connect(
  (state) => ({
    user: state.auth.user,
  })
)(GroupsImport);
